// src/main.jsx
import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { AuthProvider, useAuth } from "./context/AuthContext";

import Login from "./pages/Login.jsx";
import Dashboard from "./pages/Dashboard.jsx";
import ContractForm from "./pages/ContractForm.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import Menu from "./pages/Menu.jsx";
import Analitica from "./pages/Analitica.jsx";
import PlanDesarrollo from "./pages/PlanDesarrollo.jsx";
import Presupuesto from "./pages/presupuesto.jsx";

// ✅ NUEVO: IMPORTAR INDICADORES
import Indicadores from "./pages/Indicadores.jsx";

import SolicitudAdquisicionesMenu from "./pages/submenu.jsx";
import SolicitudAdquisiciones from "./pages/SolicitudAdquisiciones.jsx";

// ✅ ALMACÉN
import AlmacenSolicitudes from "./components/AlmacenSolicitudes.jsx";

// ✅ PAGE WRAPPER para la ruta /adquisicion/:id
import AdquisicionBienesPage from "./components/AdquisicionBienes.jsx";

// components
import InexistenciaSolicitudes from "./components/InexistenciaSolicitudes.jsx";

// pages
import InexistenciaFirmar from "./pages/InexistenciaFirmar.jsx";

import "./styles/main.css";
import "./styles/pdf-styles.css";


// ===============================
// Helpers de sesión
// ===============================
const clearLocalSession = () => {
  localStorage.removeItem("auth_user");
  localStorage.removeItem("auth_token");
};

const getStoredUser = () => {
  try {
    const raw = localStorage.getItem("auth_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    clearLocalSession();
    return null;
  }
};

const hasValidLocalSession = () => {
  const token = localStorage.getItem("auth_token");
  const u = getStoredUser();
  return Boolean(token && u);
};

const getRoleValue = (u) => {
  const name = (u?.role ?? u?.roleName ?? u?.rol ?? "").toString().trim();
  if (name) return name.toLowerCase();

  const id = u?.role_id ?? u?.rolId ?? u?.roleId ?? u?.rol_id ?? null;
  return id != null ? String(id) : "";
};


// ===============================
// ProtectedRoute
// ===============================
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div style={{ padding: 16 }}>Verificando autenticación…</div>;
  }

  const isResetPath = location.pathname === "/reset-password";
  const isLoginPath = location.pathname === "/login";

  const isAuthed = Boolean(user) || hasValidLocalSession();

  if (!isAuthed && !isResetPath && !isLoginPath) {
    return <Navigate to="/login" replace />;
  }

  return children;
}


// ===============================
// Logout
// ===============================
function LogoutRoute() {
  const navigate = useNavigate();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    clearLocalSession();

    navigate("/login", { replace: true });

  }, [navigate]);

  return <div style={{ padding: 16 }}>Cerrando sesión…</div>;
}


// ===============================
// Inexistencia wrapper
// ===============================
function InexistenciaSolicitudesRoute() {
  const { user } = useAuth();

  const effectiveUser = user || (hasValidLocalSession() ? getStoredUser() : null);
  const role = getRoleValue(effectiveUser);

  return <InexistenciaSolicitudes role={role} />;
}


// ===============================
// Home redirect
// ===============================
function HomeRedirect() {
  const { user } = useAuth();

  const localOk = hasValidLocalSession();
  const u = user || (localOk ? getStoredUser() : null);

  if (!u) return <Navigate to="/login" replace />;

  const roleId = Number(u?.role_id ?? u?.rolId ?? u?.roleId ?? NaN);

  if (roleId === 8)
    return <Navigate to="/almacen/solicitudes" replace />;

  if ([9, 10, 11].includes(roleId))
    return <Navigate to="/inexistencia/solicitudes" replace />;

  if ([2, 3, 4, 5, 6, 7].includes(roleId))
    return <Navigate to="/dashboard" replace />;

  return <Navigate to="/menu" replace />;
}


// ===============================
// App
// ===============================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="app-shell">

          <main className="app-main">

            <Routes>

              <Route path="/" element={<HomeRedirect />} />

              <Route path="/login" element={<Login />} />

              <Route path="/logout" element={<LogoutRoute />} />

              <Route
                path="/reset-password"
                element={
                  <ProtectedRoute>
                    <ResetPassword />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/menu"
                element={
                  <ProtectedRoute>
                    <Menu />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/analitica"
                element={
                  <ProtectedRoute>
                    <Analitica />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/plan-desarrollo"
                element={
                  <ProtectedRoute>
                    <PlanDesarrollo />
                  </ProtectedRoute>
                }
              />

              {/* ✅ NUEVA RUTA INDICADORES */}
              <Route
                path="/plan-desarrollo-indicadores"
                element={

                  <ProtectedRoute>
                    <Indicadores />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/presupuesto"
                element={
                  <ProtectedRoute>
                    <Presupuesto />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/contratos/nuevo"
                element={
                  <ProtectedRoute>
                    <ContractForm />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/contratos/:id"
                element={
                  <ProtectedRoute>
                    <ContractForm />
                  </ProtectedRoute>
                }
              />


              <Route
                path="/solicitud-adquisiciones"
                element={
                  <ProtectedRoute>
                    <SolicitudAdquisicionesMenu />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/solicitud-adquisiciones/solicitudes"
                element={
                  <ProtectedRoute>
                    <SolicitudAdquisiciones />
                  </ProtectedRoute>
                }
              />


              <Route
                path="/almacen/solicitudes"
                element={
                  <ProtectedRoute>
                    <AlmacenSolicitudes />
                  </ProtectedRoute>
                }
              />


              <Route
                path="/adquisicion/:id"
                element={
                  <ProtectedRoute>
                    <AdquisicionBienesPage />
                  </ProtectedRoute>
                }
              />


              <Route
                path="/inexistencia/solicitudes"
                element={
                  <ProtectedRoute>
                    <InexistenciaSolicitudesRoute />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/inexistencia/:id"
                element={
                  <ProtectedRoute>
                    <InexistenciaFirmar />
                  </ProtectedRoute>
                }
              />


              {/* Default */}
              <Route path="*" element={<HomeRedirect />} />

            </Routes>

          </main>

          <footer className="app-footer">
            <p>© {new Date().getFullYear()} - Alcaldía Municipal</p>
          </footer>

        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}


// ===============================
// Render
// ===============================
ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
